import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import NSJobComponent from '../../../job.js';

import Company from '../../../../../models/company.js';
import JobSchema from '../../../../../models/job-schema.js';

import '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';
import '@awesome.me/webawesome/dist/components/radio/radio.js';
import type WaRadioGroup from '@awesome.me/webawesome/dist/components/radio-group/radio-group.js';

import { callLambda } from '../../../../../helpers/lambda.js';
import { companiesHouseFilingUrl } from '../../../../../helpers/turnover-format.js';

import '../../../../../ns-elements/ns-job-layout.js';
import definition from '@job-schemas/company/turnover/declaration.json';
import { jobSchema } from '../../../job-schema.js';

interface TurnoverBand {
  value: string;
  label: string;
  // The lower bound of the band, used as the figure we actually store — a conservative choice
  // (never overstates turnover) given a band is all we have, not an exact amount.
  amount: number;
}

const TURNOVER_BANDS: TurnoverBand[] = [
  { value: 'MICRO', label: 'Less than £50,000', amount: 0 },
  { value: 'SMALL', label: '£50,000 - £1 million', amount: 50000 },
  { value: 'SME', label: '£1 million - £10 million', amount: 1000000 },
  { value: 'MID_MARKET', label: '£10 million - £50 million', amount: 10000000 },
  { value: 'ENTERPRISE', label: 'More than £50 million', amount: 50000000 },
];

@customElement(NSCompanyTurnoverDeclaration.is)
export default class NSCompanyTurnoverDeclaration extends NSJobComponent {
  static get is() {
    return 'ns-company-turnover-declaration';
  }

  @state()
  private __company?: Company;

  @state()
  private __selectedBand?: string;

  @state()
  private __unresolvedFilingUrl: string | null = null;

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { complete: { type: 'button', label: 'Declare' } },
    });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  async connectedCallback() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    try {
      await super.connectedCallback();

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._auth) throw new Error('Unable to find auth service');

      this.__company = await this.loadJobCompany();
      const unresolvedFilingId = this.__company.turnover?.unresolvedFilingTransactionId;
      this.__unresolvedFilingUrl = unresolvedFilingId
        ? companiesHouseFilingUrl(this.__company, unresolvedFilingId)
        : null;
    } catch (err: unknown) {
      this.showError(err);
    }
  }

  async _outcomeCompleteHandler() {
    if (!this._job || !this._job.id) {
      this.triggerCardError('Unable to find job card');
      return;
    }

    this.clearError();

    try {
      if (!this._auth) throw new Error('Missing required auth service');
      if (!this.__company) throw new Error('Missing required company');
      if (!this.__selectedBand) throw new Error('Please select a turnover band before declaring it');

      const band = TURNOVER_BANDS.find((b) => b.value === this.__selectedBand);
      if (!band) throw new Error('Unrecognised turnover band selected');

      this.loading('Declaring turnover');
      await callLambda(
        'company/turnover-outcome',
        'POST',
        this._auth.token,
        JSON.stringify({
          jobId: this._job.id,
          companyId: this.__company.id,
          turnoverAmount: band.amount,
          outcome: 'complete',
        }),
      );

      await this.outcome('complete', { turnoverAmount: String(band.amount) });
      this.loaded();
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  render() {
    return html`
      <ns-job-layout
        heading="We need a turnover figure for ${this.__company?.name ?? 'your company'}"
        image="/images/jobs/companies-house-verification.svg"
        image-label="Companies House filing illustration"
      >
        <div class="job-panel">
          ${this.__unresolvedFilingUrl
            ? html`
                <p class="note">
                  We found a filing on Companies House but couldn't automatically read a turnover figure from it —
                  <a href="${this.__unresolvedFilingUrl}" target="_blank" rel="noopener noreferrer">view it here</a>
                  to help you pick the right band below.
                </p>
              `
            : nothing}
          <wa-radio-group
            label="How much is your company's annual turnover?"
            @change=${(e: Event) => (this.__selectedBand = (e.target as WaRadioGroup).value as string)}
          >
            ${TURNOVER_BANDS.map(
              (band) => html`
                <wa-radio value=${band.value}>${band.label}</wa-radio>
              `,
            )}
          </wa-radio-group>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <p>
            We couldn't find a turnover figure from Companies House for this company. Please select the band that best
            matches your company's annual turnover.
          </p>
        </div>
      </ns-job-layout>
    `;
  }
}
